import { Info, Pencil, Trash2 } from "lucide-react";

/* ── Internal modules ───────────────────────────────────────────────────── */
import { type Column } from "../../components/g360/DataTable";
import { RBACAction } from "../../components/RBACButton";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Tip } from "../../components/ui/tooltip";

/* ── Relative imports ───────────────────────────────────────────────────── */
import { type SchedulerRow } from "./schedulerTypes";

export const RBAC_MODULE = "scheduler";

const ICON_BUTTON_CLASS = "size-7 rounded-md";

/** Serial number derived from the current page offset. */
export const serialNumberColumn = (
  page: number,
  pageSize: number,
): Column<SchedulerRow> => ({
  key: "slNo",
  header: "#",
  render: (_row, index) => page * pageSize + index + 1,
});

export const statusColumn = (
  onToggle: (row: SchedulerRow, next: boolean) => void,
  canEnableDisable: boolean,
): Column<SchedulerRow> => ({
  key: "status",
  header: "Status",
  render: (row) => (
    <div className="flex justify-center">
      <Switch
        checked={row.status}
        onCheckedChange={(next) => onToggle(row, next)}
        disabled={!canEnableDisable}
        aria-label={`Toggle status for ${row.moduleName}`}
      />
    </div>
  ),
});

export const detailsColumn = (
  onOpen: (row: SchedulerRow) => void,
): Column<SchedulerRow> => ({
  key: "details",
  header: "Details",
  render: (row) => (
    <Tip label="Details">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onOpen(row)}
        className={`${ICON_BUTTON_CLASS} text-[#64748b] hover:bg-accent hover:text-[#64748b] dark:text-white/50 dark:hover:text-white/50`}
        aria-label={`View details for ${row.moduleName}`}
      >
        <Info className="size-4" />
      </Button>
    </Tip>
  ),
});

export interface SchedulerActionPermissions {
  canUpdate: boolean;
  canDelete: boolean;
}

export const actionColumn = (
  onEdit: (row: SchedulerRow) => void,
  onDelete: (row: SchedulerRow) => void,
  { canUpdate, canDelete }: SchedulerActionPermissions,
): Column<SchedulerRow> => ({
  key: "actions",
  header: "Action",
  render: (row) => (
    <div className="flex items-center justify-center gap-1.5">
      {canUpdate && (
        <RBACAction module={RBAC_MODULE} action="edit">
          <Tip label="Edit Scheduler">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(row)}
              className={`${ICON_BUTTON_CLASS} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-600 dark:text-indigo-400 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-400`}
              aria-label={`Edit ${row.moduleName}`}
            >
              <Pencil className="size-4" />
            </Button>
          </Tip>
        </RBACAction>
      )}
      {canDelete && (
        <RBACAction module={RBAC_MODULE} action="delete">
          <Tip label="Delete Scheduler">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(row)}
              className={`${ICON_BUTTON_CLASS} text-red-600 hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-400`}
              aria-label={`Delete ${row.moduleName}`}
            >
              <Trash2 className="size-4" />
            </Button>
          </Tip>
        </RBACAction>
      )}
    </div>
  ),
});
